// @ts-nocheck
import {
    Country,
    State
} from 'country-state-city';

const getStateByName = (stateName, countryCode) => {
    const country = Country.getCountryByCode(countryCode || 'US');
    return State.getStatesOfCountry(country.isoCode)
    ?.filter(state => {
        return (state?.name === stateName)
    })
    ?.map(state => {
        return {
            label: state?.name,
            value: state?.isoCode,
        }
    })?.[0];
}

// Alternative state code mappings (what users enter -> what the library uses)
const ALTERNATIVE_STATE_CODES = {
    'UK': 'UT',  // Uttarakhand
    'CG': 'CT',  // Chhattisgarh
    'OD': 'OR',  // Odisha
    'DD': 'DH',  // Daman and Diu
};

// Indian state codes
const INDIAN_STATE_CODES = new Set([
    'AN', 'AP', 'AR', 'AS', 'BR', 'CH', 'CT', 'DL', 'GA', 'GJ', 
    'HR', 'HP', 'JK', 'JH', 'KA', 'KL', 'LA', 'LD', 'MP', 'MH', 
    'MN', 'ML', 'MZ', 'NL', 'OR', 'PY', 'PB', 'RJ', 'SK', 'TN', 
    'TG', 'TR', 'UT', 'UP', 'WB', 'DH', 'UK', 'CG', 'OD', 'DD'
]);

// City pattern indicators
const CITY_PATTERNS = {
    indian: ['abad', 'pur', 'nagar', 'puram', 'wadi', 'ganj', 'dwar', 'garh'],
    us: ['ville', 'ton', 'burg', 'field', 'port', 'land']
};

/**
 * Determines country priority based on zip code pattern
 */
const getCountryPriorityFromZip = (zipCode) => {
    if (!zipCode) return null;
    
    const zip = zipCode.toString().trim();
    if (/^\d{6}$/.test(zip)) return 'IN'; // 6-digit = India
    if (/^\d{5}(-\d{4})?$/.test(zip)) return 'US'; // 5-digit = US
    return null;
};

/**
 * Detects likely country from city name patterns
 */
const detectCountryFromCityName = (cityName) => {
    if (!cityName) return null;
    
    const lowerCity = cityName.trim().toLowerCase();
    if (CITY_PATTERNS.indian.some(pattern => lowerCity.includes(pattern))) return 'IN';
    if (CITY_PATTERNS.us.some(pattern => lowerCity.includes(pattern))) return 'US';
    return null;
};

/**
 * Searches for state code in a specific country
 */
const findStateInCountry = (countryCode, stateCode) => {
    try {
        const states = State.getStatesOfCountry(countryCode);
        return states?.find(s => s.isoCode === stateCode);
    } catch {
        return null;
    }
};

/**
 * Gets the country code based on state code, zip code, and city name
 * @param {string} stateCode - The state code (e.g., "MH", "CA", "TN")
 * @param {string} zipCode - Optional zip code to help determine country
 * @param {string} cityName - Optional city name to help determine country
 * @returns {string|null} The country code (e.g., "IN", "US") or null if not found
 */
const getCountryCodeFromLocation = (stateCode, zipCode = null, cityName = null) => {
    if (!stateCode || typeof stateCode !== 'string' || stateCode.trim().length > 2) {
        return null;
    }
    
    const trimmedCode = stateCode.trim().toUpperCase();
    const mappedCode = ALTERNATIVE_STATE_CODES[trimmedCode] || trimmedCode;
    
    // Determine priority countries
    let priorityCountries = ['IN', 'US', 'CA', 'GB', 'AU'];
    
    // Prioritize based on Indian state codes
    if (INDIAN_STATE_CODES.has(trimmedCode)) {
        priorityCountries = ['IN', ...priorityCountries.filter(c => c !== 'IN')];
    }
    
    // Override with zip code if provided
    const zipCountry = getCountryPriorityFromZip(zipCode);
    if (zipCountry) {
        return findStateInCountry(zipCountry, mappedCode) ? zipCountry : null;
    }
    
    // Override with city pattern detection
    const cityCountry = detectCountryFromCityName(cityName);
    if (cityCountry) {
        priorityCountries = [cityCountry, ...priorityCountries.filter(c => c !== cityCountry)];
    }
    
    // Search priority countries
    for (const countryCode of priorityCountries) {
        if (findStateInCountry(countryCode, mappedCode)) {
            return countryCode;
        }
    }
    
    // Search remaining countries
    try {
        for (const country of Country.getAllCountries()) {
            if (!priorityCountries.includes(country.isoCode) && findStateInCountry(country.isoCode, mappedCode)) {
                return country.isoCode;
            }
        }
    } catch {
        // Ignore errors
    }
    
    return null;
}

/**
 * Gets the full state name from a state code
 * @param {string} stateCode - The state code (e.g., "MH", "CA", "TN")
 * @param {string} zipCode - Optional zip code to help determine country
 * @param {string} cityName - Optional city name to help determine country
 * @returns {string} The full state name or the original value if not found
 */
const getStateNameFromCode = (stateCode, zipCode = null, cityName = null) => {
    if (!stateCode || typeof stateCode !== 'string') {
        return stateCode || '';
    }
    
    const trimmedCode = stateCode.trim().toUpperCase();
    
    // If it's already longer than 2 characters, assume it's already a full name
    if (trimmedCode.length > 2) {
        return trimmedCode;
    }
    
    // Map alternative codes to official codes
    const mappedCode = ALTERNATIVE_STATE_CODES[trimmedCode] || trimmedCode;
    
    // Determine priority countries and search scope
    let priorityCountries = ['IN', 'US', 'CA', 'GB', 'AU'];
    let searchOtherCountries = true;
    
    // Indian state codes get India priority
    if (INDIAN_STATE_CODES.has(trimmedCode)) {
        priorityCountries = ['IN'];
    }
    
    // Zip code takes precedence
    const zipCountry = getCountryPriorityFromZip(zipCode);
    if (zipCountry) {
        priorityCountries = [zipCountry];
        searchOtherCountries = false;
    } else {
        // City pattern detection
        const cityCountry = detectCountryFromCityName(cityName);
        if (cityCountry) {
            priorityCountries = [cityCountry];
            searchOtherCountries = false;
        }
    }
    
    // Search priority countries
    for (const countryCode of priorityCountries) {
        const foundState = findStateInCountry(countryCode, mappedCode);
        if (foundState) {
            return foundState.name;
        }
    }
    
    // Search remaining countries if allowed
    if (searchOtherCountries) {
        try {
            for (const country of Country.getAllCountries()) {
                if (!priorityCountries.includes(country.isoCode)) {
                    const foundState = findStateInCountry(country.isoCode, mappedCode);
                    if (foundState) {
                        return foundState.name;
                    }
                }
            }
        } catch {
            // Ignore errors
        }
    }
    
    // Return original code if not found
    return trimmedCode;
}

/**
 * Generates a 2-character code from a location name
 * Handles multi-word names (e.g., "New Delhi" -> "ND", "Los Angeles" -> "LA")
 * @param {string} name - Location name
 * @returns {string} 2-character uppercase code
 */
const getLocationCode = (name) => {
    if (!name || typeof name !== 'string') return '';

    const cleanName = name.trim().toUpperCase();
    const length = cleanName.length;
    
    if (length === 1) return cleanName.repeat(2);
    if (length === 2) return cleanName;
    
    // Multi-word: use first letter of first two words
    const words = cleanName.split(/[\s\-_]+/).filter(Boolean);
    if (words.length >= 2) return words[0][0] + words[1][0];
    
    // Single word: first letter + first consonant
    const vowels = new Set(['A', 'E', 'I', 'O', 'U']);
    for (let i = 1; i < length; i++) {
        const char = cleanName[i];
        if (/[A-Z]/.test(char) && !vowels.has(char)) {
            return cleanName[0] + char;
        }
    }
    
    // Fallback: first two characters
    return cleanName.substring(0, 2);
};

/**
 * Generates a 2-character code from a city name
 * @param {string} cityName - City name
 * @returns {string} 2-character code
 */
const getCityCode = (cityName) => getLocationCode(cityName);

/**
 * Generates a 2-character code from a state name
 * @param {string} stateName - State name
 * @returns {string} 2-character code
 */
const getStateCode = (stateName) => {
    if (!stateName || typeof stateName !== 'string') return '';
    
    const trimmed = stateName.trim();
    return trimmed.length <= 2 ? trimmed.toUpperCase() : getLocationCode(trimmed);
};

/**
 * Formats a contractor's location (city and state) into a standardized suffix string.
 * This is a utility function that combines state and city codes with consistent formatting.
 * 
 * @param {Object} options - The location data
 * @param {string} options.state - The state name
 * @param {string} options.city - The city name
 * @returns {string} Formatted location suffix (e.g., ", CA, LA" or ", CA" or "") 
 * 
 * @example
 * // Returns ", CA, LA"
 * getLocationSuffix({ state: "California", city: "Los Angeles" })
 * 
 * @example
 * // Returns ", CA"
 * getLocationSuffix({ state: "California", city: "" })
 * 
 * @example
 * // Returns ""
 * getLocationSuffix({ state: "", city: "" })
 */
const getLocationSuffix = ({ state = '', city = '' } = {}) => {
    const stateCode = state ? getStateCode(state) : '';
    const cityCode = city ? getCityCode(city) : '';
    
    if (!stateCode && !cityCode) return '';
    
    return `, ${[stateCode, cityCode].filter(Boolean).join(', ')}`;
};

export { getStateByName, getCityCode, getStateCode, getStateNameFromCode, getCountryCodeFromLocation, getLocationSuffix };